import { useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { MOTION_STYLES, useCountUp } from './visualMotion';
import {
  blockCorners,
  convexHull,
  PALLET_BASE_MM,
  projectIso,
  sortForLoading,
  sortForPainting,
  type IsoBlock,
  type IsoDims,
  type IsoPoint,
} from './isometricLoad';

interface SupplyIsometricLoadViewProps {
  dims: IsoDims;
  blocks: IsoBlock[];
  overflow?: number;
  transportMode: 'road' | 'sea';
  selectedProductId: number | null;
  onSelectProduct: (productId: number | null) => void;
  indexByProduct: Map<number | null, number>;
  illustrative?: boolean;
  /** Productos que no llegan a un pallet y por eso no se dibujan en el reparto ilustrativo. */
  smallProducts?: number;
  fillPct: number | null;
  /** Peso de la carga en la unidad de la organización. */
  weight: number | null;
  weightUnit: string;
  expanded?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/** Color del cartón: se mezcla con el color del producto para que cada bloque se lea como cajas sin perder el código de color. */
const CARTON = '#d9b07a';
const BOX_HEIGHT_GUESS_MM = 280;

/** Reparto de las cajas de un piso (a lo largo × en profundidad) que deja la caja más proporcionada, sin huecos. */
function boxGrid(count: number, lengthMm: number, widthMm: number) {
  if (!(count > 0)) return { across: 2, deep: 1 };
  let best = { across: Math.ceil(count), deep: 1, score: Number.POSITIVE_INFINITY };
  for (let deep = 1; deep <= 6; deep += 1) {
    const across = Math.ceil(count / deep);
    const boxL = lengthMm / across;
    const boxW = widthMm / deep;
    const score = Math.max(boxL, boxW) / Math.min(boxL, boxW) + 3 * (across * deep - count);
    if (score < best.score) best = { across, deep, score };
  }
  return { across: Math.min(12, best.across), deep: best.deep };
}

function clampNumber(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

const CAB_LENGTH_MM = 2300;
// Tractora con remolque: el piso del remolque queda a ~1,15 m del suelo y las ruedas apoyan en él.
const GROUND_Z = -1150;
const WHEEL_RADIUS_MM = 500;
const WHEEL_Z = GROUND_Z + WHEEL_RADIUS_MM;
const WHEEL_THICKNESS_MM = 320;
const CAB_COLOR = '#3d6f9e';
const CAB_DARK = '#2f5a82';
const CHASSIS_COLOR = '#1e293b';
const VIEW_WIDTH = 700;

const STYLES = `
@keyframes iso-drop{0%{opacity:0;transform:translateY(-54px)}62%{opacity:1;transform:translateY(3px)}100%{opacity:1;transform:translateY(0)}}
@keyframes iso-march{to{stroke-dashoffset:-20}}
@keyframes iso-wave{from{transform:translateX(0)}to{transform:translateX(-64px)}}
@keyframes iso-rise{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
.iso-block{animation:iso-drop .55s cubic-bezier(.2,.8,.3,1) backwards;transition:opacity .25s ease,filter .2s ease;cursor:pointer;outline:none}
.iso-block:hover,.iso-block:focus-visible{filter:brightness(1.13)}
.iso-selected{stroke-dasharray:6 4;animation:iso-march 1s linear infinite}
.iso-wave{animation:iso-wave 4s linear infinite}
.iso-hud{animation:iso-rise .4s ease both}
@keyframes iso-road{to{stroke-dashoffset:-48}}
.iso-road{stroke-dasharray:18 14;animation:iso-road 1.4s linear infinite}
.iso-free{stroke-dasharray:6 5;animation:iso-march 1.6s linear infinite}
@media (prefers-reduced-motion:reduce){.iso-block,.iso-selected,.iso-wave,.iso-hud,.iso-road,.iso-free{animation:none}}
${MOTION_STYLES}`;

function points(list: IsoPoint[], scale: number, origin: IsoPoint): string {
  return list.map(([x, y]) => `${((x - origin[0]) * scale).toFixed(1)},${((y - origin[1]) * scale).toFixed(1)}`).join(' ');
}

export function SupplyIsometricLoadView({
  dims,
  blocks,
  overflow = 0,
  transportMode,
  selectedProductId,
  onSelectProduct,
  indexByProduct,
  illustrative = false,
  smallProducts = 0,
  fillPct,
  weight: weightValue,
  weightUnit,
  expanded = false,
  t,
}: SupplyIsometricLoadViewProps) {
  const [replay, setReplay] = useState(0);
  const [hover, setHover] = useState<{ block: IsoBlock; x: number; y: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { length, width, height } = dims;
  const road = transportMode === 'road';

  const geometry = useMemo(() => {
    const cab = road ? CAB_LENGTH_MM : 0;
    const hull = transportMode === 'sea' ? 700 : 0;
    const extents: IsoPoint[] = [];
    const roadMargin = road ? 600 : 0;
    [-cab - roadMargin, length + roadMargin].forEach((x) => [0, width + (road ? 800 : 0)].forEach((y) => [-(road ? 1250 : hull), height + 150].forEach((z) => {
      extents.push(projectIso(x, y, z));
    })));
    const xs = extents.map((p) => p[0]);
    const ys = extents.map((p) => p[1]);
    const padding = Math.max(length, width) * 0.035;
    const minX = Math.min(...xs) - padding;
    const maxX = Math.max(...xs) + padding;
    const minY = Math.min(...ys) - padding;
    const maxY = Math.max(...ys) + padding * 2.2;
    const scale = VIEW_WIDTH / (maxX - minX);
    return { scale, origin: [minX, minY] as IsoPoint, viewHeight: (maxY - minY) * scale };
  }, [length, width, height, road, transportMode]);

  const { scale, origin, viewHeight } = geometry;
  const P = (x: number, y: number, z: number) => projectIso(x, y, z);
  const poly = (list: IsoPoint[]) => points(list, scale, origin);
  const at = (x: number, y: number, z: number): IsoPoint => {
    const [sx, sy] = P(x, y, z);
    return [(sx - origin[0]) * scale, (sy - origin[1]) * scale];
  };

  const painted = useMemo(() => sortForPainting(blocks), [blocks]);
  const loadingOrder = useMemo(() => {
    const order = new Map<string, number>();
    sortForLoading(blocks).forEach((block, index) => order.set(block.key, index));
    return order;
  }, [blocks]);

  const usedLength = blocks.reduce((max, block) => (block.z === 0 ? Math.max(max, block.x + block.l) : max), 0);
  const freePct = blocks.length > 0 ? ((length - usedLength) / length) * 100 : 0;
  const legend = useMemo(() => {
    const byProduct = new Map<number | null, { productId: number | null; label: string; color: string; pallets: number }>();
    blocks.forEach((block) => {
      const entry = byProduct.get(block.productId);
      const share = block.partialFill ?? 1;
      if (entry) entry.pallets += share;
      else byProduct.set(block.productId, { productId: block.productId, label: block.label, color: block.color, pallets: share });
    });
    return [...byProduct.values()].sort((a, b) => b.pallets - a.pallets);
  }, [blocks]);
  const palletTotal = useMemo(() => new Set(blocks.map((block) => block.palletId ?? block.key)).size, [blocks]);
  const pallets = useCountUp(palletTotal);
  const fill = useCountUp(fillPct);
  const weight = useCountUp(weightValue);

  const renderBox = (
    key: string,
    box: { x: number; y: number; z: number; l: number; w: number; h: number },
    color: string,
    opts: { top?: number; right?: number; front?: number; carton?: number } = {},
  ) => {
    const { x, y, z, l, w, h } = box;
    const top = [P(x, y, z + h), P(x + l, y, z + h), P(x + l, y + w, z + h), P(x, y + w, z + h)];
    const right = [P(x + l, y, z), P(x + l, y + w, z), P(x + l, y + w, z + h), P(x + l, y, z + h)];
    const front = [P(x, y + w, z), P(x + l, y + w, z), P(x + l, y + w, z + h), P(x, y + w, z + h)];
    return (
      <g key={key} stroke="#0f172a" strokeOpacity="0.28" strokeWidth="0.5" strokeLinejoin="round">
        <polygon points={poly(front)} fill={color} />
        {opts.carton ? <polygon points={poly(front)} fill={CARTON} fillOpacity={opts.carton} /> : null}
        <polygon points={poly(front)} fill="#0f172a" fillOpacity={opts.front ?? 0.14} />
        <polygon points={poly(right)} fill={color} />
        {opts.carton ? <polygon points={poly(right)} fill={CARTON} fillOpacity={opts.carton} /> : null}
        <polygon points={poly(right)} fill="#0f172a" fillOpacity={opts.right ?? 0.3} />
        <polygon points={poly(top)} fill={color} />
        {opts.carton ? <polygon points={poly(top)} fill={CARTON} fillOpacity={opts.carton} /> : null}
        <polygon points={poly(top)} fill="#ffffff" fillOpacity={opts.top ?? 0.22} />
      </g>
    );
  };

  // Cajas dentro del bloque: pisos, columnas de frente y de costado.
  const renderBoxGrid = (block: (typeof painted)[number], baseHeight: number, inset: number) => {
    const x = block.x + inset;
    const y = block.y + inset;
    const z = block.z + baseHeight;
    const l = block.l - inset * 2;
    const w = block.w - inset * 2;
    const h = Math.max(10, block.h - baseHeight);
    const rows = clampNumber(Math.round(block.partialFill != null ? h / BOX_HEIGHT_GUESS_MM : block.layers ?? h / BOX_HEIGHT_GUESS_MM), 1, 24);
    const { across, deep } = boxGrid(block.boxesPerLayer ?? 0, l, w);
    const showColumns = (l * scale) / across >= 5;
    const showRows = (h * scale) / rows >= 3.5;
    const stroke = { stroke: '#5b3d1e', strokeOpacity: 0.42, strokeWidth: 0.4, fill: 'none', pointerEvents: 'none' as const };
    const lines: JSX.Element[] = [];
    if (showRows) {
      for (let row = 1; row < rows; row += 1) {
        const zz = z + (h * row) / rows;
        lines.push(<polyline key={`r${row}`} points={poly([P(x, y + w, zz), P(x + l, y + w, zz), P(x + l, y, zz)])} {...stroke} />);
      }
    }
    if (showColumns) {
      for (let col = 1; col < across; col += 1) {
        const xx = x + (l * col) / across;
        lines.push(<polyline key={`f${col}`} points={poly([P(xx, y + w, z), P(xx, y + w, z + h), P(xx, y, z + h)])} {...stroke} />);
      }
      for (let col = 1; col < deep; col += 1) {
        const yy = y + (w * col) / deep;
        lines.push(<polyline key={`s${col}`} points={poly([P(x + l, yy, z), P(x + l, yy, z + h), P(x, yy, z + h)])} {...stroke} />);
      }
    }
    return lines.length > 0 ? <g pointerEvents="none">{lines}</g> : null;
  };

  // Rueda en el plano lateral (y constante): neumático con grosor, llanta y buje.
  const renderWheel = (key: string, centerX: number, planeY: number, centerZ: number, radius: number) => {
    const ring = (ringRadius: number, y: number) => Array.from({ length: 28 }, (_, index) => {
      const angle = (index / 28) * Math.PI * 2;
      return P(centerX + Math.cos(angle) * ringRadius, y, centerZ + Math.sin(angle) * ringRadius);
    });
    return (
      <g key={key} stroke="#0f172a" strokeOpacity="0.5" strokeWidth="0.4" pointerEvents="none">
        <polygon points={poly(ring(radius, planeY - WHEEL_THICKNESS_MM))} fill="#020617" />
        <polygon points={poly(ring(radius, planeY))} fill="#0f172a" />
        <polygon points={poly(ring(radius * 0.58, planeY))} fill="#94a3b8" />
        <polygon points={poly(ring(radius * 0.26, planeY))} fill="#475569" />
      </g>
    );
  };

  const floor = [P(0, 0, 0), P(length, 0, 0), P(length, width, 0), P(0, width, 0)];
  const backWall = [P(0, 0, 0), P(length, 0, 0), P(length, 0, height), P(0, 0, height)];
  const headWall = [P(0, 0, 0), P(0, width, 0), P(0, width, height), P(0, 0, height)];
  const ribCount = Math.max(6, Math.round(length / 450));
  const slotLines = Math.max(1, Math.floor(length / 1200));
  // Tres ejes juntos cerca del final del remolque (separados 1,3 m).
  const axles = road ? [length - 3000, length - 1700, length - 400].map((x) => Math.max(x, length * 0.3)) : [];
  const dimensionLabelStyle = { fontSize: 9, fill: '#64748b', fontWeight: 600 } as const;
  const lengthLabel = road ? at(length / 2, width, 0) : at(length / 2, width, height);
  const lengthLabelOffset = road ? 18 : -8;
  const heightLabel = at(length, width, height / 2);

  return (
    <div className="relative" ref={wrapperRef}>
      <style>{STYLES}</style>
      <div className="pointer-events-none absolute left-2 top-2 z-10 flex flex-wrap gap-1.5 text-[11px]" aria-live="polite">
        <span className="iso-hud rounded-full bg-white/90 px-2.5 py-1 font-semibold tabular-nums text-slate-800 shadow-sm ring-1 ring-slate-200">
          {t('supply.supplyAgent.loadIsoPallets', { count: Math.round(pallets ?? 0) })}
        </span>
        {fill != null ? (
          <span className="iso-hud rounded-full bg-white/90 px-2.5 py-1 font-semibold tabular-nums text-sky-800 shadow-sm ring-1 ring-slate-200" style={{ animationDelay: '80ms' }}>
            {t('supply.supplyAgent.loadIsoVolume', { value: fill.toLocaleString(undefined, { maximumFractionDigits: 1 }) })}
          </span>
        ) : null}
        {weight != null ? (
          <span className="iso-hud rounded-full bg-white/90 px-2.5 py-1 font-semibold tabular-nums text-slate-700 shadow-sm ring-1 ring-slate-200" style={{ animationDelay: '160ms' }}>
            {t('supply.supplyAgent.loadIsoWeight', { value: Math.round(weight).toLocaleString(), unit: weightUnit })}
          </span>
        ) : null}
        {illustrative ? (
          <span className="iso-hud rounded-full bg-white/90 px-2.5 py-1 font-medium text-slate-600 ring-1 ring-slate-200" style={{ animationDelay: '240ms' }}>
            {t('supply.supplyAgent.loadIsoIllustrative')}
          </span>
        ) : null}
        {smallProducts > 0 ? (
          <span className="iso-hud rounded-full bg-slate-50 px-2.5 py-1 font-medium text-slate-600 ring-1 ring-slate-200" style={{ animationDelay: '280ms' }}>
            {t('supply.supplyAgent.loadIsoSmallProducts', { count: smallProducts })}
          </span>
        ) : null}
        {overflow > 0 ? (
          <span className="iso-hud rounded-full bg-rose-50 px-2.5 py-1 font-medium text-rose-800 ring-1 ring-rose-200" style={{ animationDelay: '320ms' }}>
            {t('supply.supplyAgent.loadIsoOverflow', { count: overflow })}
          </span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => setReplay((value) => value + 1)}
        className="absolute right-2 top-2 z-10 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white/90 px-2.5 py-1.5 text-[11px] font-medium text-slate-700 shadow-sm hover:bg-white"
      >
        <RotateCcw size={13} aria-hidden="true" />
        {t('supply.supplyAgent.loadIsoReplay')}
      </button>

      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${viewHeight.toFixed(1)}`}
        role="img"
        aria-label={t('supply.supplyAgent.loadIsoAria', { count: blocks.length })}
        className={`mx-auto h-auto w-full ${expanded ? 'max-h-[80vh] max-w-none' : 'max-h-[420px] max-w-5xl'}`}
        onClick={() => onSelectProduct(null)}
      >
        <defs>
          <linearGradient id="isoWall" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e2e8f0" stopOpacity="0.95" />
            <stop offset="1" stopColor="#cbd5e1" stopOpacity="0.75" />
          </linearGradient>
          <filter id="isoBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.2" />
          </filter>
          <linearGradient id="isoFloor" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#f1f5f9" />
            <stop offset="1" stopColor="#dbe4ee" />
          </linearGradient>
        </defs>

        {transportMode === 'sea' ? (
          <g>
            <polygon points={poly([P(-500, width + 250, -650), P(length + 500, width + 250, -650), P(length + 500, width + 250, 0), P(-500, width + 250, 0)])} fill="#1e3a5f" />
            <polygon points={poly([P(length + 500, -250, -650), P(length + 500, width + 250, -650), P(length + 500, width + 250, 0), P(length + 500, -250, 0)])} fill="#16304d" />
            <g opacity="0.55" stroke="#7dd3fc" strokeWidth="1.2" fill="none">
              <g className="iso-wave">
                {Array.from({ length: 14 }, (_, index) => {
                  const [wx, wy] = at(-400 + index * (length / 10), width + 330, -520);
                  return <path key={index} d={`M ${wx} ${wy} q 16 -5 32 0 t 32 0`} />;
                })}
              </g>
            </g>
          </g>
        ) : (
          <g>
            <polygon points={poly([P(-CAB_LENGTH_MM - 500, -200, GROUND_Z), P(length + 500, -200, GROUND_Z), P(length + 500, width + 950, GROUND_Z), P(-CAB_LENGTH_MM - 500, width + 950, GROUND_Z)])} fill="#cbd5e1" fillOpacity="0.45" />
            <path className="iso-road" d={`M ${at(-CAB_LENGTH_MM - 500, width + 700, GROUND_Z).join(' ')} L ${at(length + 500, width + 700, GROUND_Z).join(' ')}`} stroke="#94a3b8" strokeWidth="1.6" fill="none" />
            <polygon points={poly([P(-CAB_LENGTH_MM, 150, GROUND_Z), P(length + 150, 150, GROUND_Z), P(length + 150, width + 250, GROUND_Z), P(-CAB_LENGTH_MM, width + 250, GROUND_Z)])} fill="#0f172a" fillOpacity="0.2" filter="url(#isoBlur)" pointerEvents="none" />
            {/* Chasis: largueros bajo el piso, de la cabina al paragolpes trasero, y pata de apoyo del remolque. */}
            {renderBox('chassis', { x: -CAB_LENGTH_MM + 100, y: width * 0.22, z: -440, l: length + CAB_LENGTH_MM - 250, w: width * 0.56, h: 270 }, CHASSIS_COLOR, { top: 0.05, right: 0.3, front: 0.12 })}
            {renderBox('landing-gear', { x: 1800, y: width * 0.7, z: GROUND_Z, l: 120, w: 130, h: -GROUND_Z - 440 }, '#475569', { top: 0.1, right: 0.3, front: 0.15 })}
            {(() => {
              const cabTop = Math.min(height + 100, 2900);
              const sideY = width - 60;
              const window = [P(-1520, sideY, cabTop - 1000), P(-560, sideY, cabTop - 1000), P(-560, sideY, cabTop - 260), P(-1520, sideY, cabTop - 260)];
              return (
                <g>
                  {renderBox('bumper', { x: -CAB_LENGTH_MM - 80, y: 160, z: -560, l: 90, w: width - 320, h: 420 }, '#0f172a', { top: 0.15, right: 0.3, front: 0.2 })}
                  {renderBox('hood', { x: -CAB_LENGTH_MM, y: 240, z: -440, l: 650, w: width - 480, h: 1500 }, CAB_DARK, { top: 0.2, right: 0.3, front: 0.12 })}
                  {renderBox('cab', { x: -1650, y: 60, z: -440, l: 1350, w: width - 120, h: cabTop + 440 }, CAB_COLOR, { top: 0.2, right: 0.32, front: 0.1 })}
                  {renderBox('deflector', { x: -560, y: 180, z: cabTop, l: 280, w: width - 360, h: 440 }, '#e2e8f0', { top: 0.3, right: 0.25, front: 0.1 })}
                  <polygon points={poly(window)} fill="#bae6fd" fillOpacity="0.9" stroke="#0f172a" strokeOpacity="0.35" strokeWidth="0.5" />
                  <path d={`M ${at(-1040, sideY, -330).join(' ')} L ${at(-1040, sideY, cabTop - 1100).join(' ')}`} stroke="#0f172a" strokeOpacity="0.3" strokeWidth="0.6" fill="none" />
                  {renderBox('exhaust', { x: -280, y: width - 190, z: -300, l: 90, w: 90, h: cabTop + 700 }, '#94a3b8', { top: 0.3, right: 0.3, front: 0.15 })}
                  {[-1900, -700].map((x) => renderWheel(`cab-wheel-${x}`, x, width - 40, WHEEL_Z, WHEEL_RADIUS_MM))}
                </g>
              );
            })()}
          </g>
        )}

        {renderBox('slab', { x: 0, y: 0, z: -170, l: length, w: width, h: 170 }, '#64748b', { top: 0, right: 0.34, front: 0.2 })}
        <polygon points={poly(floor)} fill="url(#isoFloor)" stroke="#94a3b8" strokeOpacity="0.55" strokeWidth="0.6" />
        {Array.from({ length: slotLines + 1 }, (_, index) => {
          const x = Math.min(length, index * 1200);
          return <path key={`slot-${index}`} d={`M ${at(x, 0, 0).join(' ')} L ${at(x, width, 0).join(' ')}`} stroke="#94a3b8" strokeOpacity="0.28" strokeWidth="0.5" />;
        })}
        <polygon points={poly(backWall)} fill="url(#isoWall)" stroke="#94a3b8" strokeOpacity="0.7" strokeWidth="0.8" />
        {Array.from({ length: ribCount - 1 }, (_, index) => {
          const x = ((index + 1) * length) / ribCount;
          return <path key={`rib-${index}`} d={`M ${at(x, 0, 0).join(' ')} L ${at(x, 0, height).join(' ')}`} stroke="#94a3b8" strokeOpacity="0.35" strokeWidth="0.5" />;
        })}
        <polygon points={poly(headWall)} fill="url(#isoWall)" stroke="#94a3b8" strokeOpacity="0.7" strokeWidth="0.8" />

        {axles.map((x) => renderWheel(`axle-${x}`, x, width, WHEEL_Z, WHEEL_RADIUS_MM))}
        {road ? (
          <g>
            {renderBox('rear-bar', { x: length - 160, y: 150, z: -760, l: 110, w: width - 300, h: 170 }, '#334155', { top: 0.1, right: 0.3, front: 0.2 })}
            {[[60, 260], [width - 260, width - 60]].map(([y0, y1]) => (
              <polygon key={`tail-${y0}`} points={poly([P(length, y0, -140), P(length, y1, -140), P(length, y1, -40), P(length, y0, -40)])} fill="#ef4444" fillOpacity="0.9" stroke="#0f172a" strokeOpacity="0.3" strokeWidth="0.4" />
            ))}
          </g>
        ) : null}

        {blocks.length > 0 && freePct > 2 ? (() => {
          const region = [P(usedLength, 0, 0), P(length, 0, 0), P(length, width, 0), P(usedLength, width, 0)];
          const label = at((usedLength + length) / 2, width / 2, 0);
          return (
            <g pointerEvents="none">
              <polygon className="sv-pulse" points={poly(region)} fill="#38bdf8" fillOpacity="0.13" />
              <polygon className="iso-free" points={poly(region)} fill="none" stroke="#0284c7" strokeOpacity="0.55" strokeWidth="1" />
              <text x={label[0]} y={label[1] + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#0369a1">
                {t('supply.supplyAgent.loadIsoFreeSpace', { pct: freePct.toLocaleString(undefined, { maximumFractionDigits: 0 }) })}
              </text>
            </g>
          );
        })() : null}

        <g filter="url(#isoBlur)" opacity="0.2" pointerEvents="none">
          {blocks.filter((block) => block.z === 0).map((block) => (
            <polygon key={`shadow-${block.key}`} points={poly([P(block.x + 60, block.y + 60, 0), P(block.x + block.l + 110, block.y + 60, 0), P(block.x + block.l + 110, block.y + block.w + 110, 0), P(block.x + 60, block.y + block.w + 110, 0)])} fill="#0f172a" />
          ))}
        </g>

        <g key={replay}>
          {painted.map((block) => {
            const dimmed = selectedProductId != null && selectedProductId !== block.productId;
            const isSelected = selectedProductId != null && selectedProductId === block.productId;
            const baseHeight = block.sharedPallet ? 0 : block.partialFill != null ? PALLET_BASE_MM : Math.min(PALLET_BASE_MM, block.h * 0.2);
            const inset = Math.min(block.l, block.w) * 0.02;
            const order = loadingOrder.get(block.key) ?? 0;
            const outline = isSelected ? convexHull(blockCorners(block)) : [];
            const topCenter = at(block.x + block.l / 2, block.y + block.w / 2, block.z + block.h);
            const index = indexByProduct.get(block.productId);
            return (
              <g
                key={block.key}
                className="iso-block"
                style={{ animationDelay: `${Math.min(order * 26, 1500)}ms`, opacity: dimmed ? 0.22 : 1 }}
                role="button"
                tabIndex={0}
                aria-label={`${block.label} · ${t('supply.supplyAgent.loadIsoLevel', { level: block.level })}`}
                aria-pressed={isSelected}
                onMouseMove={(event) => {
                  const box = wrapperRef.current?.getBoundingClientRect();
                  if (box) setHover({ block, x: event.clientX - box.left, y: event.clientY - box.top });
                }}
                onMouseLeave={() => setHover(null)}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelectProduct(isSelected ? null : block.productId);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectProduct(isSelected ? null : block.productId);
                  }
                }}
              >
                {baseHeight > 0 ? renderBox(`${block.key}-base`, { x: block.x, y: block.y, z: block.z, l: block.l, w: block.w, h: baseHeight }, '#b8834d', { top: 0.1, right: 0.34, front: 0.2 }) : null}
                {renderBox(
                  `${block.key}-cargo`,
                  {
                    x: block.x + inset,
                    y: block.y + inset,
                    z: block.z + baseHeight,
                    l: block.l - inset * 2,
                    w: block.w - inset * 2,
                    h: Math.max(10, block.h - baseHeight),
                  },
                  block.color,
                  { carton: 0.42 },
                )}
                {renderBoxGrid(block, baseHeight, inset)}
                {block.fragile ? <circle cx={topCenter[0]} cy={topCenter[1]} r="3.2" fill="#fef3c7" stroke="#b45309" strokeWidth="0.8" /> : null}
                {index != null && !block.fragile && block.l * scale * 0.9 > 26 ? (
                  <text x={topCenter[0]} y={topCenter[1] + 3} textAnchor="middle" fontSize="8" fontWeight="800" fill="#ffffff" stroke="#0f172a" strokeOpacity="0.45" strokeWidth="0.4" paintOrder="stroke">
                    {index}
                  </text>
                ) : null}
                {isSelected ? <polygon className="iso-selected" points={poly(outline)} fill="none" stroke="#0f172a" strokeWidth="1.8" pointerEvents="none" /> : null}
              </g>
            );
          })}
        </g>

        <polygon points={poly([P(0, width, 0), P(length, width, 0), P(length, width, height), P(0, width, height)])} fill="#e2e8f0" fillOpacity="0.1" stroke="#94a3b8" strokeOpacity="0.35" strokeWidth="0.5" pointerEvents="none" />
        <path d={`M ${at(0, width, height).join(' ')} L ${at(length, width, height).join(' ')} L ${at(length, 0, height).join(' ')}`} fill="none" stroke="#64748b" strokeOpacity="0.55" strokeWidth="0.9" strokeDasharray="4 3" pointerEvents="none" />
        <path d={`M ${at(0, 0, height).join(' ')} L ${at(0, width, height).join(' ')}`} fill="none" stroke="#64748b" strokeOpacity="0.55" strokeWidth="0.9" strokeDasharray="4 3" pointerEvents="none" />
        <path d={`M ${at(length, width, 0).join(' ')} L ${at(length, width, height).join(' ')}`} fill="none" stroke="#64748b" strokeOpacity="0.55" strokeWidth="0.9" strokeDasharray="4 3" pointerEvents="none" />
        <text x={lengthLabel[0]} y={lengthLabel[1] + lengthLabelOffset} textAnchor="middle" {...dimensionLabelStyle}>{(length / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} m</text>
        <text x={heightLabel[0] + 8} y={heightLabel[1]} textAnchor="start" {...dimensionLabelStyle}>{(height / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} m</text>

        {blocks.length === 0 ? (
          <text x={VIEW_WIDTH / 2} y={viewHeight / 2} textAnchor="middle" fontSize="11" fontWeight="700" fill="#334155">
            {t('supply.supplyAgent.loadPlacementEmpty')}
          </text>
        ) : null}
      </svg>

      {hover ? (
        <div
          className="pointer-events-none absolute z-20 w-56 -translate-y-full rounded-lg border border-slate-200 bg-white/95 p-2.5 text-[11px] shadow-lg backdrop-blur"
          style={{ left: Math.max(4, Math.min(hover.x + 12, (wrapperRef.current?.clientWidth ?? 600) - 232)), top: Math.max(56, hover.y - 10) }}
        >
          <p className="flex items-center gap-1.5 font-semibold text-slate-900">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: hover.block.color }} />
            <span className="truncate">{hover.block.label}</span>
          </p>
          <p className="mt-1 tabular-nums text-slate-600">
            {Math.round(hover.block.l)} × {Math.round(hover.block.w)} × {Math.round(hover.block.h)} mm
          </p>
          <p className="text-slate-500">
            {hover.block.partialFill != null
              ? t('supply.supplyAgent.loadIsoPartial', { pct: Math.round(hover.block.partialFill * 100) })
              : t('supply.supplyAgent.loadIsoLevel', { level: hover.block.level })}
            {hover.block.stackSize > 1 ? ` · ${t('supply.supplyAgent.loadIsoStack', { count: hover.block.stackSize })}` : ''}
            {hover.block.fragile ? ` · ${t('supply.supplyAgent.loadIsoFragile')}` : ''}
          </p>
          {hover.block.layers && hover.block.partialFill == null ? (
            <p className="text-slate-500">
              {hover.block.boxesPerLayer
                ? t('supply.supplyAgent.loadIsoLayers', { layers: hover.block.layers, boxes: hover.block.boxesPerLayer })
                : t('supply.supplyAgent.loadIsoLayersOnly', { layers: hover.block.layers })}
            </p>
          ) : null}
        </div>
      ) : null}

      {legend.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5 px-1 pb-1" aria-label={t('supply.supplyAgent.loadIsoLegend')}>
          {legend.slice(0, 10).map((entry, index) => {
            const active = selectedProductId != null && selectedProductId === entry.productId;
            return (
              <li key={String(entry.productId ?? entry.label)} className="sv-rise" style={{ animationDelay: `${index * 40}ms` }}>
                <button
                  type="button"
                  aria-pressed={active}
                  onClick={() => onSelectProduct(active ? null : entry.productId)}
                  className={`inline-flex max-w-[220px] items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] transition-colors ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: entry.color }} />
                  <span className="truncate">{indexByProduct.get(entry.productId) ? `${indexByProduct.get(entry.productId)} · ` : ''}{entry.label}</span>
                  <span className="tabular-nums opacity-70">×{(Math.round(entry.pallets * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                </button>
              </li>
            );
          })}
          {legend.length > 10 ? <li className="px-1 py-1 text-[11px] text-slate-500">+{legend.length - 10}</li> : null}
        </ul>
      ) : null}
    </div>
  );
}
